import style from './Sidebar.module.scss';
import className from 'classnames/bind';
import {memo, useContext, useMemo, useState} from "react";
import Tippy from '@tippyjs/react/headless';
import {useDispatch, useSelector} from "react-redux";
import {Menu} from "antd";
import {useLocation, Link} from "react-router-dom";
import {Image, FontAwesomeIcon, Button, PopperWrapper, Icon} from "~/components";
import {currentUserSelector, authActions} from "~/reduxs/Auth/authSlice";
import {useGetMySubscriptionQuery} from "~/reduxs/api/subscriptionApiSlice";
import {AppContext} from "~/context/AppProvider";
import {globalNavigate} from "~/routes/GlobalHistory";
import {resolveUserAvatar, isLoginAs, exitLoginAs} from "~/utils";
import {useCan} from "~/hooks";
import {LoginAsModal} from "../LoginAs";
import NotificationBell from "../NotificationBell/NotificationBell";
import {NavBarData} from "../NavBar/NavBarData";
import images from "../../assets/images";

const cn = className.bind(style);

// Số hotline hỗ trợ
const HOTLINE = '0799890462';

function findActiveKey(navItems, pathname)
{
	const allItems = [];

	for (const item of navItems)
	{
		if (!item) continue;

		if (item.to) allItems.push({key: item.key, to: item.to});

		if (Array.isArray(item.children))
		{
			for (const child of item.children)
			{
				if (child && child.to) allItems.push({key: child.key, to: child.to});
			}
		}
	}

	if (allItems.length === 0) return "home";

	allItems.sort((a, b) => b.to.length - a.to.length);

	for (const item of allItems)
	{
		if (pathname.startsWith(item.to)) return item.key;
	}

	return "home";
}

function Sidebar({onNavigate})
{
	const location = useLocation();

	const dispatch = useDispatch();

	const currentUser = useSelector(currentUserSelector);

	const {rolesData} = useContext(AppContext);

	const canLoginAs = useCan('login_as');

	const loginAsActive = isLoginAs();

	// Gói + token còn lại của user (hiển thị ở sidebar).
	const {data: sub} = useGetMySubscriptionQuery();

	const [openLoginAs, setOpenLoginAs] = useState(false);

	// Trạng thái mở/thu gọn các nhóm menu (submenu). Module nghiệp vụ mới có thể
	// thêm key mặc định mở vào mảng này.
	const [openKeys, setOpenKeys] = useState([]);

	const handleLogout = () => {
		localStorage.clear();
		dispatch(authActions.logout());
		globalNavigate(process.env.REACT_APP_HOMEPAGE + "/login");
	}

	const openLoginAsModal = () => { setOpenLoginAs(true); onNavigate && onNavigate(); }

	const renderAccountMenu = () => (
		<PopperWrapper className={cn('account-action')}>
			<Button leftIcon={Icon.user} className={cn('button')} to={'/profile'} onClick={onNavigate}> Thông tin tài khoản</Button>
			<Button leftIcon={Icon.lock} className={cn('button')} to={'/account/password'} onClick={onNavigate}> Đổi mật khẩu</Button>
			{/* Quay lại tài khoản gốc chỉ hiện khi đang mạo danh; không cần quyền */}
			{loginAsActive &&
				<Button leftIcon={Icon.arrowRotateLeft} onClick={exitLoginAs} className={cn('button')}> Quay lại tài khoản gốc</Button>
			}
			{/* Đăng nhập / chuyển sang tài khoản khác — quyền xét trên tài khoản gốc */}
			{(canLoginAs || loginAsActive) &&
				<Button leftIcon={Icon.userSwitch} onClick={openLoginAsModal} className={cn('button')}> Đăng nhập tài khoản khác</Button>
			}
			<hr />
			<Button leftIcon={Icon.logout} onClick={handleLogout} className={cn('button')}> Đăng xuất </Button>
		</PopperWrapper>
	)

	const navBarList = NavBarData();

	const moduleActive = useMemo(
		() => findActiveKey(navBarList, location.pathname),
		[location.pathname, navBarList]
	);

	// Chức vụ: map role key của user sang tên trong rolesData
	const roleName = useMemo(() => {
		if (!currentUser?.role) return null;
		if (rolesData && !Array.isArray(rolesData)) return rolesData[currentUser.role] || currentUser.role;
		return currentUser.role;
	}, [currentUser, rolesData]);

	// Thông tin gói + token còn lại cho thẻ ở sidebar.
	const nf = (n) => Number(n || 0).toLocaleString('vi-VN');
	const isRootUser = Boolean(sub?.is_root);
	const ownKeyUser = Boolean(sub?.require_own_ai_key);
	const tk = sub?.ai_tokens || {};
	const tkPct = tk.limit > 0 ? Math.min(100, Math.round(((tk.used || 0) / tk.limit) * 100)) : 0;

	return (
		<div className={cn('sidebar')}>
			{/* Logo */}
			<div className={cn('logo')}>
				<Image src={images.logo} alt="Omni SEO" />
			</div>

			{/* Hồ sơ: avatar - tên - chức vụ + chuông thông báo */}
			<div className={cn('profile-row')}>
				<Tippy interactive render={renderAccountMenu} placement="bottom" offset={[0, 8]}>
					<div className={cn('profile')}>
						<div className={cn('avatar')}>
							<Image src={resolveUserAvatar(currentUser)} alt={currentUser?.firstname} />
						</div>
						<div className={cn('profile-info')}>
							<p className={cn('profile-name')}>
								{currentUser?.lastname} {currentUser?.firstname}
							</p>
							{roleName && <p className={cn('profile-role')}>{roleName}</p>}
						</div>
						<FontAwesomeIcon icon="fa-light fa-chevron-down" className={cn('profile-caret')} />
					</div>
				</Tippy>
				<NotificationBell onNavigate={onNavigate} />
			</div>

			{/* Thẻ Gói & token còn lại → tới trang Gói & Hạn mức */}
			{sub && (
				<Link to="/subscription" className={cn('plan-box')} data-plan={sub.plan?.slug} onClick={onNavigate}>
					<div className={cn('plan-box-head')}>
						<span className={cn('plan-box-name')}>
							<FontAwesomeIcon icon="fa-light fa-crown" /> {sub.plan?.name || 'Miễn phí'}
						</span>
						<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" className={cn('plan-box-go')} />
					</div>
					{isRootUser ? (
						<div className={cn('plan-box-line')}><span>AI Token</span><strong>Không giới hạn</strong></div>
					) : ownKeyUser ? (
						<div className={cn('plan-box-line')}><span>AI Token</span><strong>Khoá riêng</strong></div>
					) : (
						<>
							<div className={cn('plan-box-line')}>
								<span>Token còn lại</span>
								<strong>{nf(tk.remaining)}</strong>
							</div>
							<div className={cn('plan-box-bar')}>
								<div className={cn('plan-box-fill')} style={{width: `${tkPct}%`}} />
							</div>
						</>
					)}
				</Link>
			)}

			{/* Menu điều hướng */}
			<div className={cn('menu')}>
				<Menu
					mode="inline"
					selectedKeys={[moduleActive]}
					openKeys={openKeys}
					onOpenChange={setOpenKeys}
					items={navBarList}
					onClick={onNavigate}
					style={{borderInlineEnd: 'none', background: 'transparent'}}
				/>
			</div>

			{/* Khung hỗ trợ */}
			<div className={cn('support')}>
				<div className={cn('support-inner')}>
					<div className={cn('support-icon')}>
						<FontAwesomeIcon icon="fa-light fa-headset" />
					</div>
					<p className={cn('support-title')}>Bạn cần hỗ trợ?</p>
					<p className={cn('support-desc')}>Liên hệ hotline để được trợ giúp</p>
					<a href={`tel:${HOTLINE.replace(/\s/g, '')}`} className={cn('support-hotline')}>
						<FontAwesomeIcon icon="fa-light fa-phone-volume" />
						<span>{HOTLINE}</span>
					</a>
				</div>
			</div>

			<LoginAsModal open={openLoginAs} onClose={() => setOpenLoginAs(false)} />
		</div>
	);
}

export default memo(Sidebar);
